import { useMutation } from "@tanstack/react-query";
import { MoreHorizontal } from "lucide-react";
import { useState } from "react";
import { useTranslations } from "use-intl";

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@lawline/ui/components/alert-dialog";
import { Button } from "@lawline/ui/components/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@lawline/ui/components/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@lawline/ui/components/tooltip";

import { useAuth } from "@platform/auth/context";
import { setUserRoleMutation } from "../users.mutations";
import type { AdminUser } from "../users.types";

// Toggles the cross-tenant admin role. A grant of staff access is consequential,
// so an alert dialog confirms it. The viewer's own row has no action, because an
// admin cannot demote itself. The server enforces that too.
export function UserRoleActions({ user }: { user: AdminUser }) {
	const t = useTranslations("admin.users.actions");
	const tActions = useTranslations("common.actions");
	const { user: current } = useAuth();
	const setRole = useMutation(setUserRoleMutation);
	const [confirmOpen, setConfirmOpen] = useState(false);

	if (user.id === current?.id) return null;

	const isAdmin = user.role === "admin";
	const nextRole = isAdmin ? "user" : "admin";

	return (
		<>
			<DropdownMenu>
				<Tooltip>
					<TooltipTrigger asChild>
						<DropdownMenuTrigger asChild>
							<Button variant="ghost" size="icon-xs">
								<MoreHorizontal />
							</Button>
						</DropdownMenuTrigger>
					</TooltipTrigger>
					<TooltipContent>{tActions("more")}</TooltipContent>
				</Tooltip>
				<DropdownMenuContent align="end">
					<DropdownMenuItem
						variant={isAdmin ? "destructive" : undefined}
						onClick={() => setConfirmOpen(true)}
					>
						{isAdmin ? t("revokeAdmin") : t("makeAdmin")}
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>

			<AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t(isAdmin ? "revokeConfirmTitle" : "makeConfirmTitle", {
								name: user.name || user.email,
							})}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t(isAdmin ? "revokeConfirmDescription" : "makeConfirmDescription")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("confirmCancel")}</AlertDialogCancel>
						<AlertDialogAction
							variant={isAdmin ? "destructive" : undefined}
							onClick={() => setRole.mutate({ id: user.id, role: nextRole })}
						>
							{t("confirmConfirm")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}
